import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";
import {
  useAllInterviewReports,
  useReportById,
} from "../../../../store/query/scheduleInterviewQuery";
import { useDownloadTestReport } from "../../../../store/query/scheduleProgrammingTestQuery";
import { debounce } from "lodash";
import { mergePdfBlobs } from "../../../../utils/pdfMerger";

export const useCandidateReports = () => {
  const tableDataRef = useRef(null);
  const [downloading, setDownloading] = useState({});
  const { showToast } = useToast();
  const [filterName, setFilterName] = useState("");
  const [isLastPage, setIsLastPage] = useState(false);

  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    limit: 5,
    status: "Completed",
    search: "",
    interviewType: "All",
  });

  const { data, isLoading, isError, error } = useAllInterviewReports({
    page: filterOptions.page,
    limit: filterOptions.limit,
    status: filterOptions.status,
    search: filterOptions.search,
    interviewType: filterOptions.interviewType,
  });

  const reportMutation = useReportById();
  const codingReportMutation = useDownloadTestReport();
  const interviews = data?.data || [];

  const handleNextPage = () => {
    const hasMoreData = interviews.length === filterOptions.limit;
    if (hasMoreData) {
      setFilterOptions((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  const handlePrevPage = () => {
    setFilterOptions((prev) => ({ ...prev, page: prev.page - 1 }));
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setFilterName(value);
    debouncedFilterChange(value);
  };

  const handleRowsPerPageChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      limit: event.target.value,
      page: 1,
    }));

  const debouncedFilterChange = useCallback(
    debounce((value) => {
      setFilterOptions((prev) => ({
        ...prev,
        search: value,
        page: 1,
      }));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  useEffect(() => {
    if (data && data.data) {
      setIsLastPage(data.data.length < filterOptions.limit);
    }
  }, [data, filterOptions.limit]);

  const handleInterviewTypeChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      interviewType: event.target.value,
      page: 1,
    }));

  useEffect(() => {
    setIsLastPage(false);
  }, [filterOptions.search, filterOptions.status, filterOptions.interviewType]);

  const triggerDownload = (blobData, filename) => {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const handleDownloadReport = async (candidateId, candidateName, interviewType, aiInterviewId) => {
    setDownloading((prev) => ({ ...prev, [candidateId]: true }));
    let genTimer = setTimeout(() => {
      showToast("Report is being generated. Please wait...", "info");
    }, 1500);

    try {
      if (interviewType === "AI Bot +") {
        let codingBlob = null;
        let aiBlob = null;

        try {
          codingBlob = await codingReportMutation.mutateAsync(candidateId);
        } catch (err) {
          console.error("Failed to download coding report:", err);
        }

        try {
          if (aiInterviewId) {
            aiBlob = await reportMutation.mutateAsync(aiInterviewId);
          }
        } catch (err) {
          console.error("Failed to download AI report:", err);
        }

        if (!codingBlob && !aiBlob) {
          throw new Error("Neither coding nor AI report is ready yet");
        }

        const mergedBlob = await mergePdfBlobs(codingBlob, aiBlob);
        if (!mergedBlob || mergedBlob.size === 0) {
          throw new Error("Failed to generate combined PDF report");
        }

        triggerDownload(mergedBlob, `candidate_report_${candidateName}.pdf`);

        const wasOnDemand = codingBlob?.wasGenerated || aiBlob?.wasGenerated;
        if (codingBlob && aiBlob) {
          showToast(
            wasOnDemand
              ? "Combined report generated and downloaded successfully!"
              : "Combined report downloaded successfully!",
            "success"
          );
        } else if (codingBlob) {
          showToast(
            "Coding report downloaded. AI Interview report is not ready yet.",
            "warning"
          );
        } else {
          showToast(
            "AI Interview report downloaded. Coding report is not ready yet.",
            "warning"
          );
        }
      } else {
        const isCodingType = interviewType === "Coding Test";
        const blobData = isCodingType
          ? await codingReportMutation.mutateAsync(candidateId)
          : await reportMutation.mutateAsync(candidateId);
        if (!blobData || blobData.size === 0) {
          throw new Error("Received empty file from server");
        }
        triggerDownload(blobData, `candidate_report_${candidateName}.pdf`);
        showToast(
          blobData.wasGenerated
            ? "Report generated and downloaded successfully!"
            : "Report downloaded successfully!",
          "success"
        );
      }
    } catch (error) {
      console.error("Download failed:", error);
      showToast(
        error?.message || "Failed to download the report. The report is not ready yet.",
        "error"
      );
    } finally {
      clearTimeout(genTimer);
      setDownloading((prev) => ({ ...prev, [candidateId]: false }));
    }
  };

  const handleResetFilters = () => {
    setFilterName("");
    setFilterOptions((prev) => ({
      ...prev,
      search: "",
      interviewType: "All",
      page: 1,
    }));
  };

  return {
    tableDataRef,
    downloading,
    interviews,
    isLoading,
    isError,
    error,
    isLastPage,
    filterName,
    filterOptions,
    handleNextPage,
    handlePrevPage,
    handleFilterChange,
    handleRowsPerPageChange,
    handleInterviewTypeChange,
    handleDownloadReport,
    handleResetFilters,
  };
};
